import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const ProblemFormModal = ({ problem, onClose, onSubmit }) => {
  const [projects, setProjects] = useState([]);
  const normalizePriority = (value) => {
    const priority = `${value || ''}`.trim().toLowerCase();
    if (priority === 'critical') return 'Critical';
    if (priority === 'high') return 'High';
    if (priority === 'medium') return 'Medium';
    if (priority === 'low') return 'Low';
    return 'Medium';
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    domain: 'app_development',
    priority: 'Medium',
    status: 'Not Decided',
    dueDate: '',
    notes: '',
    solutions: [],
    subtasks: [],
    project_id: ''
  });
  const [doneDate, setDoneDate] = useState(
    problem?.done_date ? new Date(problem.done_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await supabase
          .from('experiments')
          .select('id, title')
          .eq('type', 'project')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setProjects(data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    
    fetchProjects();
  }, []);

  useEffect(() => {
    const loadProblemData = async () => {
      if (problem) {
        const basicData = {
          ...problem,
          dueDate: problem.due_date || problem.dueDate || '',
          priority: normalizePriority(problem.priority),
          solutions: [],
          subtasks: []
        };

        if (problem.done_date) {
          setDoneDate(new Date(problem.done_date).toISOString().split('T')[0]);
        } else {
          setDoneDate(new Date().toISOString().split('T')[0]);
        }

        const isEditMode = !!(problem && problem.id);

        if (isEditMode) {
          if (problem.solutions && problem.solutions.length > 0) {
            basicData.solutions = problem.solutions
              .filter(sol => sol && sol.description && sol.description.trim() !== '')
              .map(sol => ({
                description: sol.description.trim(),
                isChosen: sol.is_chosen || false
              }));
          } else {
            basicData.solutions = [];
          }

          if (problem.subtasks && problem.subtasks.length > 0) {
            basicData.subtasks = problem.subtasks
              .filter(st => st && st.title && st.title.trim() !== '')
              .map(st => ({
                id: st.id,
                title: st.title.trim(),
                due_date: st.due_date || '',
                is_completed: st.is_completed || false,
                solution: st.solution || '',
                assigned_to: st.assigned_to || null,
                completed_date: st.completed_date || null
              }));
          } else {
            basicData.subtasks = [];
          }
        }
        setFormData(basicData);
      } else {
        setFormData({
          title: '',
          description: '',
          domain: 'app_development',
          priority: 'Medium',
          status: 'Not Decided',
          dueDate: '',
          notes: '',
          solutions: [],
          subtasks: []
        });
        setDoneDate(new Date().toISOString().split('T')[0]);
      }
    };

    loadProblemData();
  }, [problem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddSolution = () => {
    setFormData({
      ...formData,
      solutions: [{ description: '', isChosen: false }, ...formData.solutions]
    });
  };

  const handleRemoveSolution = (index) => {
    setFormData({
      ...formData,
      solutions: formData.solutions.filter((_, i) => i !== index)
    });
  };

  const handleSolutionChange = (index, field, value) => {
    const updated = formData.solutions.map((sol, i) => {
      if (i === index) {
        return { ...sol, [field]: value };
      }
      return sol;
    });
    setFormData({ ...formData, solutions: updated });
  };

  const handleAddSubtask = () => {
    setFormData({
      ...formData,
      subtasks: [{ title: '', due_date: '', is_completed: false, solution: '', assigned_to: null, completed_date: null }, ...formData.subtasks]
    });
  };

  const handleRemoveSubtask = (index) => {
    setFormData({
      ...formData,
      subtasks: formData.subtasks.filter((_, i) => i !== index)
    });
  };

  const handleSubtaskChange = (index, field, value) => {
    const updated = formData.subtasks.map((st, i) => {
      if (i === index) {
        if (field === 'is_completed') {
          return { 
            ...st, 
            [field]: value,
            completed_date: value ? new Date().toISOString() : null
          };
        }
        return { ...st, [field]: value };
      }
      return st;
    });
    setFormData({ ...formData, subtasks: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    if (loading) return;
    setLoading(true);

    try {
      const validSolutions = formData.solutions.filter(sol =>
        sol && sol.description && sol.description.trim() !== ''
      );
      const validSubtasks = formData.subtasks.filter(st =>
        st && st.title && st.title.trim() !== ''
      );

      const submissionData = {
        ...formData,
        solutions: validSolutions,
        subtasks: validSubtasks,
        done_date: formData.status === 'Done' ? doneDate : null
      };

      await onSubmit(submissionData);
    } catch (error) {
      console.error(`Error in handleSubmit:`, error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-form-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold" data-testid="form-modal-title">
              {problem ? 'Edit Problem' : 'Create New Problem'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="form-modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Title <span className="text-white opacity-50">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full glass-input"
              placeholder="Enter problem title"
              required
              data-testid="form-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="4"
              placeholder="Enter detailed description (optional)"
              data-testid="form-description-input"
            />
          </div>

          {/* Domain & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Domain <span className="text-white opacity-50">*</span>
              </label>
              <div className="relative">
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-domain-select"
                >
                <option value="app_development" className="bg-black text-white">App Development</option>
                <option value="frontend" className="bg-black text-white">Frontend</option>
                <option value="backend" className="bg-black text-white">Backend</option>
                <option value="cloud" className="bg-black text-white">Cloud</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Priority <span className="text-white opacity-50">*</span>
              </label>
              <div className="relative">
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-priority-select"
                >
                  <option value="Critical" className="bg-black text-white">Critical</option>
                  <option value="Low" className="bg-black text-white">Low</option>
                  <option value="Medium" className="bg-black text-white">Medium</option>
                  <option value="High" className="bg-black text-white">High</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Link to Project <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <div className="relative">
              <select
                name="project_id"
                value={formData.project_id || ''}
                onChange={handleChange}
                className="w-full glass-input cursor-pointer pr-10"
                data-testid="form-project-select"
              >
                <option value="" className="bg-black text-white">No Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id} className="bg-black text-white">
                    {project.title}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-xs text-gray-500 mt-1">Link this task to a project for better tracking</p>
          </div>

          {/* Status & Due Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Status <span className="text-white opacity-50">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-status-select"
                >
                  <option value="Not Decided" className="bg-black text-white">Not Decided</option>
                  <option value="In Progress" className="bg-black text-white">In Progress</option>
                  <option value="Hold" className="bg-black text-white">Hold</option>
                  <option value="Done" className="bg-black text-white">Done</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full glass-input"
                data-testid="form-due-date-input"
              />
            </div>
          </div>

          {/* Done Date */}
          {formData.status === 'Done' && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-400">Completed On</label>
              <input
                type="date"
                value={doneDate}
                onChange={(e) => setDoneDate(e.target.value)}
                className="bg-black border border-white/20 text-white text-sm rounded-md px-3 py-2 outline-none focus:border-white transition-colors"
                data-testid="form-done-date-input"
              />
            </div>
          )}

          {/* Solutions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">Solutions</label>
            </div>
            <button
              type="button"
              onClick={handleAddSolution}
              className="w-full py-2 border border-dashed border-white/20 rounded-lg text-gray-400 hover:text-white hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm mb-3"
              data-testid="add-solution-btn"
            >
              <Plus size={16} />
              Add Solution
            </button>
            <div className="space-y-3 mb-3">
              {formData.solutions.map((solution, index) => (
                <div key={index} className="glass-card p-4" data-testid={`solution-form-${index}`}>
                  <textarea
                    value={solution.description}
                    onChange={(e) => handleSolutionChange(index, 'description', e.target.value)}
                    className="w-full glass-input resize-none mb-2"
                    rows="2"
                    placeholder="Enter solution description"
                    data-testid={`solution-description-${index}`}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={solution.isChosen}
                        onChange={(e) => handleSolutionChange(index, 'isChosen', e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`solution-chosen-${index}`}
                      />
                      <span className="text-sm">Chosen Solution</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveSolution(index)}
                      className="text-white hover:text-gray-300 opacity-70"
                      data-testid={`solution-remove-${index}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">Subtasks</label>
            </div>
            <button
              type="button"
              onClick={handleAddSubtask}
              className="w-full py-2 border border-dashed border-white/20 rounded-lg text-gray-400 hover:text-white hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm mb-3"
              data-testid="add-subtask-btn"
            >
              <Plus size={16} />
              Add Subtask
            </button>
            <div className="space-y-3 mb-3">
              {formData.subtasks.map((subtask, originalIndex) => (
                <div key={originalIndex} className="glass-card p-4" data-testid={`subtask-form-${originalIndex}`}>
                  <input
                    type="text"
                    value={subtask.title}
                    onChange={(e) => handleSubtaskChange(originalIndex, 'title', e.target.value)}
                    className="w-full glass-input mb-2"
                    placeholder="Enter subtask title"
                    data-testid={`subtask-title-${originalIndex}`}
                  />
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-400 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={subtask.due_date}
                        onChange={(e) => handleSubtaskChange(originalIndex, 'due_date', e.target.value)}
                        className="w-full glass-input text-sm"
                        data-testid={`subtask-due-date-${originalIndex}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubtask(originalIndex)}
                      className="text-white hover:text-gray-300 opacity-70 mt-5"
                      data-testid={`subtask-remove-${originalIndex}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subtask.is_completed}
                        onChange={(e) => handleSubtaskChange(originalIndex, 'is_completed', e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`subtask-completed-${originalIndex}`}
                      />
                      <span className="text-sm">Completed</span>
                    </label>
                  </div>
                  {subtask.is_completed && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Completed Date</label>
                        <input
                          type="date"
                          value={subtask.completed_date ? new Date(subtask.completed_date).toISOString().split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString();
                            handleSubtaskChange(originalIndex, 'completed_date', dateValue);
                          }}
                          className="w-full glass-input text-sm"
                          data-testid={`subtask-completed-date-${originalIndex}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Solution / Outcome</label>
                        <textarea
                          value={subtask.solution || ''}
                          onChange={(e) => handleSubtaskChange(originalIndex, 'solution', e.target.value)}
                          className="w-full glass-input text-sm"
                          rows="2"
                          placeholder="Describe what was done to complete this subtask..."
                          data-testid={`subtask-solution-${originalIndex}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="Enter additional notes"
              data-testid="form-notes-input"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
            data-testid="form-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 rounded font-medium transition-colors ${loading
              ? 'bg-gray-600 text-gray-200 cursor-not-allowed'
              : 'bg-white hover:bg-gray-200 text-black'
              }`}
            data-testid="form-save-btn"
          >
            {loading ? 'Saving...' : (problem ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProblemFormModal;
